import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { addActionNameToMutationResult } from 'shared/hooks/utils/add-action-name-to-mutation-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import type { ApiError } from 'shared/hooks/utils/types';
import { z } from 'zod/v4';

const URL = 'mails/drafts';

const CreateDraftSchema = z.object({
  threadId: z.string().optional(),
  subject: z.string(),
  body: z.string(),
  toMail: z.array(z.string()),
  ccMail: z.array(z.string()).optional(),
  bccMail: z.array(z.string()).optional(),
});

export type CreateDraftVariables = z.infer<typeof CreateDraftSchema>;

const CreateDraftResponseSchema = z.object({
  id: z.coerce.string(),
  threadId: z.coerce.string(),
});

type CreateDraftResponse = z.infer<typeof CreateDraftResponseSchema>;

async function createDraftFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateDraftVariables,
): Promise<CreateDraftResponse> {
  const instance = await axiosInstance;
  const response = await instance.post(URL, CreateDraftSchema.parse(variables));

  return CreateDraftResponseSchema.parse(response.data);
}

export function useCreateDraft(
  options: MutationOptions<CreateDraftResponse, ApiError, CreateDraftVariables> = {},
) {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<CreateDraftResponse, ApiError, CreateDraftVariables>({
    mutationFn: variables => createDraftFn(axiosInstance, variables),
    onSuccess: data => {
      client.invalidateQueries({ queryKey: ['mail', data.threadId] });
      client.invalidateQueries({ queryKey: ['mails', 'inbox'], refetchType: 'all' });
      client.invalidateQueries({ queryKey: ['mails', 'drafts'], refetchType: 'all' });
    },
    ...options,
  });

  return addActionNameToMutationResult<
    CreateDraftResponse,
    ApiError,
    CreateDraftVariables,
    'createDraft'
  >('createDraft', mutation);
}
